import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { Fee } from "@modules/main/components/step-summary/sending-modal/entities/fee.model";
import { SendTransaction } from "@models/transaction";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { Network } from "@modules/+new/payments/features/crypto-deposit/domain/entities/network.model";
import { ModalService } from "@core-services/modal.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Fiat } from "@models";
import { AccountService } from "@modules/account/services/account.service";
import { DestinationTagType } from "@modules/+new/payments/features/crypto-deposit/domain/entities/destination-tag-type";
import { TranslationService } from "@core-services/translation.service";
import { SendModalComponent } from "@modules/main/components/step-summary/sending-modal/send-modal/send-modal.component";
import { SendSelectRecipientComponent } from "@modules/main/components/step-summary/sending-modal/send-select-recipient/send-select-recipient.component";
import { SendAmountComponent } from "@modules/main/components/step-summary/sending-modal/send-amount/send-amount.component";
import { UserService } from "@main-services/user.service";
import { SendMultichainTwoFaComponent } from "@modules/main/components/step-summary/sending-modal/send-multichain-two-fa/send-multichain-two-fa.component";
import { MultichainSendService } from "@modules/main/components/step-summary/sending-modal/services/multichain-send.service";
import { SendTrackingFacade } from "@modules/main/components/step-summary/sending-modal/services/send-tracking.facade";
import { Big } from "big.js";
import { AddressBookWalletFacade } from "@modules/main/components/address-book-wallet/services/address-book-wallet.facade";
import { take } from "rxjs/operators";
import { TinShieldEntryHelper } from "@modules/taxes/tin/services/tin-shield-entry.helper";

@Component({
	selector: "app-send-multichain-summary",
	templateUrl: "./send-multichain-summary.component.html",
	styleUrls: ["./send-multichain-summary.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [MultichainSendService],
	standalone: false
})
export class SendMultichainSummaryComponent implements OnInit {
	@Input() fee: Fee = null;
	@Input() sendTransaction: SendTransaction = null;
	@Input() selectedCryptocoin: PaymentsCryptocoin = null;
	@Input() selectedNetwork: Network = null;
	@Input() contactName: string = "";
	@Input() selectedHost?: string = "";
	@Input() legalEntityIdentifier?: string = "";
	public wallet: TradableWallet = null;
	public monogram: string = "";
	public fiat: Fiat = null;
	public termsAccepted: boolean = false;
	public maskedAddress: string = "";
	public maskedContact: string = "";
	public DTType: string = "";
	public totalCoin: string = "";
	public onBehalfOf: string = "";

	constructor(
		private readonly modalService: ModalService,
		private readonly walletListService: TradableWalletListService,
		private readonly accountService: AccountService,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		private readonly multichainSendService: MultichainSendService,
		public readonly trackingFacade: SendTrackingFacade,
		private readonly walletFacade: AddressBookWalletFacade,
		private readonly tinShieldEntryHelper: TinShieldEntryHelper
	) {}

	ngOnInit(): void {
		this.trackingFacade.trackSummaryScreenViewed(this.selectedCryptocoin?.name);
		this.wallet = this.walletListService.getItemById(this.sendTransaction?.getWalletId());
		this.monogram = this.getMonogram().toUpperCase();
		this.fiat = this.accountService.getDefaultFiat();
		this.maskedAddress = this.getMaskedAddress(this.sendTransaction.getRecipient());
		this.DTType = this.getDTType();
		this.maskedContact = this.getMaskedContact();
		this.totalCoin = new Big(this.sendTransaction.getAmountCoin()).plus(new Big(this.fee.fee)).toString();
		this.walletFacade.onBehalfOf.subscribe((onBehalfOf: string) => (this.onBehalfOf = onBehalfOf));
	}

	public get shouldDisplayLEI(): boolean {
		return !!(this.legalEntityIdentifier && this.legalEntityIdentifier.trim().length > 0);
	}

	private getMaskedContact(): string {
		if (this.contactName.length > 20) {
			return this.contactName.slice(0, 20) + "...";
		}
		return this.contactName;
	}

	private getMaskedAddress(address: string): string {
		if (address.length > 20) {
			return address.substring(0, 9) + "..." + address.slice(-9);
		}
		return address;
	}

	public onBackButtonClick(): void {
		this.modalService.pop();
	}

	public onCancelButtonClick(): void {
		this.modalService.popAll();
	}

	public clickedAssetCard(): void {
		this.tinShieldEntryHelper
			.handleTinStatusCheck$()
			.pipe(take(1))
			.subscribe({
				next: (isTinFrozen) => {
					if (isTinFrozen) {
						return;
					}
				}
			});
		const newSendTran = new SendTransaction();
		this.onCancelButtonClick();
		this.modalService
			.create(SendModalComponent, { isBackFromMultichain: true, data: { transaction: newSendTran } })
			.present();
	}

	public clickedContactCard(): void {
		this.modalService
			.create(SendSelectRecipientComponent, {
				selectedCryptocoin: this.selectedCryptocoin,
				selectedNetwork: this.selectedNetwork,
				accountWalletId: this.wallet.getId()
			})
			.present();
	}

	public clickedAmountCard(): void {
		const modal = this.modalService.create(SendAmountComponent, {
			selectedNetwork: this.selectedNetwork,
			selectedCryptocoin: this.selectedCryptocoin,
			sendTransaction: this.sendTransaction,
			contactName: this.contactName,
			fee: this.fee
		});
		modal.present();
	}

	public isContact(): boolean {
		return this.contactName !== "external";
	}

	private getMonogram(): string {
		if (this.contactName === "external") {
			return "";
		}
		const names = this.contactName.split(" ");
		return names.length > 1 ? names[0].charAt(0) + names[1].charAt(0) : names[0].charAt(0);
	}

	public onToggleClick(event: boolean): void {
		this.termsAccepted = event;
	}

	private getDTType(): string {
		if (this.selectedNetwork.destination_tag?.type === DestinationTagType.MESSAGE) {
			return this.translationService.translate("xem_destination_tag");
		}
		if (this.selectedNetwork.destination_tag?.type === DestinationTagType.MEMO) {
			return this.translationService.translate("stellar_memo");
		}
		return this.translationService.translate("destination_tag");
	}

	public transfer(): void {
		this.trackingFacade.trackSummaryTransferButtonClicked();
		this.sendTransaction["amount"] = this.sendTransaction.getAmountCoin();
		if (this.userService.user.get2FA()) {
			this.modalService
				.create(SendMultichainTwoFaComponent, {
					sendTransaction: this.sendTransaction,
					selectedCryptocoin: this.selectedCryptocoin,
					walletId: this.wallet.getId(),
					selectedNetwork: this.selectedNetwork
				})
				.present();
		} else {
			this.multichainSendService.finishTravelRuleWithdrawal(
				this.sendTransaction.getAddressBookWalletId(),
				this.sendTransaction.getAmountCoin(),
				this.wallet.getId(),
				this.selectedNetwork,
				this.selectedCryptocoin,
				this.onBehalfOf
			);
		}
	}
}
